import React from 'react';
import { Badge } from '../ui/badge';
import { 
  Shield, 
  Lock, 
  FileCheck, 
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  User,
  Users
} from 'lucide-react';

// Governance Step Display
export const GovernanceStep = ({ step, isExpanded = false }) => {
  const gateTypeLabels = {
    identity_verification: 'Identity Verification',
    authority_check: 'Authority Check',
    consent_check: 'Consent Check',
    disclosure_gate: 'Disclosure Gate',
    handoff_confirmation: 'Handoff Confirmation',
    exception_escalation: 'Exception Escalation',
    journey_validation: 'Journey Validation',
    assistance_request: 'Assistance Request',
    station_readiness: 'Station Readiness',
    operator_confirmation: 'Operator Confirmation',
    referral_entry: 'Referral Entry',
    pathway_assignment: 'Pathway Assignment',
    clinical_review: 'Clinical Review',
    appointment_readiness: 'Appointment Readiness',
    discharge_handoff: 'Discharge Handoff'
  };

  const roleLabels = {
    passenger: 'Passenger',
    station_staff: 'Station Staff',
    train_operator: 'Train Operator',
    assistance_coordinator: 'Assistance Coordinator',
    patient: 'Patient',
    general_practitioner: 'GP',
    specialist: 'Specialist',
    care_coordinator: 'Care Coordinator',
    nurse: 'Nurse',
    system: 'System',
    reviewer: 'Reviewer'
  };

  return (
    <div className={`border rounded-lg ${step.gate_passed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step.gate_passed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
            }`}>
              {step.step_number}
            </div>
            <div>
              <div className="text-white font-medium text-sm">
                {gateTypeLabels[step.gate_type] || step.gate_type}
              </div>
              <div className="text-slate-500 text-xs">
                {roleLabels[step.actor_role] || step.actor_role}
              </div>
            </div>
          </div>
          <Badge className={step.gate_passed ? 'bg-emerald-500' : 'bg-red-500'}>
            {step.gate_passed ? 'Passed' : 'Blocked'}
          </Badge>
        </div>

        {isExpanded && (
          <div className="space-y-3 mt-4 pt-4 border-t border-slate-700">
            {/* Authority Check */}
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-cyan-400 mt-0.5" />
              <div>
                <div className="text-xs text-slate-500">Authority Checked</div>
                <div className="text-sm text-slate-300">{step.authority_checked}</div>
              </div>
            </div>

            {/* Disclosure */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <Eye className="w-4 h-4 text-emerald-400 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500">Disclosed</div>
                  <div className="text-xs text-emerald-400">
                    {step.information_disclosed?.join(', ') || 'None'}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <EyeOff className="w-4 h-4 text-slate-500 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500">Withheld</div>
                  <div className="text-xs text-slate-400">
                    {step.information_withheld?.join(', ') || 'None'}
                  </div>
                </div>
              </div>
            </div>

            {/* Outcome */}
            <div className="bg-slate-800/50 rounded p-2">
              <div className="text-xs text-slate-500">Outcome</div>
              <div className="text-sm text-white">{step.outcome}</div>
            </div>

            {/* Receipt */}
            {step.receipt_hash && (
              <div className="text-xs text-slate-500">
                Receipt: <code className="text-cyan-400">{step.receipt_hash}</code>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Handoff Display
export const HandoffDisplay = ({ handoff }) => {
  const roleLabels = {
    passenger: 'Passenger',
    station_staff: 'Station Staff',
    train_operator: 'Train Operator',
    assistance_coordinator: 'Assistance Coordinator',
    patient: 'Patient',
    general_practitioner: 'GP',
    specialist: 'Specialist',
    care_coordinator: 'Care Coordinator',
    nurse: 'Nurse',
    system: 'System'
  };

  return (
    <div className="border border-cyan-500/30 bg-cyan-500/5 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-300">{handoff.from_actor}</span>
          <span className="text-xs text-slate-500">({roleLabels[handoff.from_role]})</span>
        </div>
        <ArrowRight className="w-4 h-4 text-cyan-400" />
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-300">{handoff.to_actor}</span>
          <span className="text-xs text-slate-500">({roleLabels[handoff.to_role]})</span>
        </div>
      </div>

      <div className="text-sm text-slate-400 mb-3">{handoff.purpose}</div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-slate-500 mb-1">Minimum Necessary Disclosure</div>
          <div className="space-y-1">
            {handoff.minimum_disclosure?.map((item, i) => (
              <div key={i} className="flex items-center gap-1 text-emerald-400">
                <CheckCircle2 className="w-3 h-3" />
                {item}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-slate-500 mb-1">Excluded from Disclosure</div>
          <div className="space-y-1">
            {handoff.excluded_from_disclosure?.map((item, i) => (
              <div key={i} className="flex items-center gap-1 text-slate-500">
                <XCircle className="w-3 h-3" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
        <div className="flex items-center gap-2">
          {handoff.acknowledged ? (
            <Badge className="bg-emerald-500 text-xs">Acknowledged</Badge>
          ) : (
            <Badge className="bg-amber-500 text-xs">Pending</Badge>
          )}
        </div>
        {handoff.receipt_hash && (
          <code className="text-xs text-cyan-400">{handoff.receipt_hash}</code>
        )}
      </div>
    </div>
  );
};

// Custody Record Display
export const CustodyDisplay = ({ record }) => {
  const roleLabels = {
    passenger: 'Passenger',
    station_staff: 'Station Staff',
    train_operator: 'Train Operator',
    assistance_coordinator: 'Assistance Coordinator',
    patient: 'Patient',
    general_practitioner: 'GP',
    specialist: 'Specialist',
    care_coordinator: 'Care Coordinator',
    nurse: 'Nurse',
    system: 'System'
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-slate-800/30 rounded">
      <div className="flex items-center gap-3">
        <Lock className="w-4 h-4 text-slate-500" />
        <div>
          <div className="text-sm text-white">{record.data_type}</div>
          <div className="text-xs text-slate-500">
            Held by: {roleLabels[record.custodian_role]}
          </div>
        </div>
      </div>
      <div className="text-right">
        {record.retained_locally ? (
          <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
            Retained locally
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-400">
            Disclosed: {record.disclosed_to}
          </Badge>
        )}
      </div>
    </div>
  );
};

// Reviewer Panel - Shows governance details
export const ReviewerPanel = ({ workflow, title = "Governance View" }) => {
  if (!workflow) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-slate-400 border-b border-slate-700 pb-3">
        <Shield className="w-5 h-5 text-cyan-400" />
        <span className="font-medium text-white">{title}</span>
        <Badge variant="outline" className="ml-auto text-xs">
          {workflow.workflow_id}
        </Badge>
      </div>

      {/* Summary */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <div className="text-xs text-slate-500 mb-1">What was shared</div>
        <div className="text-sm text-slate-300">{workflow.shared_with_summary}</div>
      </div>

      {/* Custody Records */}
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">
          Data Custody
        </div>
        <div className="space-y-2">
          {workflow.custody_records?.map((record, i) => (
            <CustodyDisplay key={i} record={record} />
          ))}
        </div>
      </div>

      {/* Governance Steps */}
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">
          Governance Steps
        </div>
        <div className="space-y-2">
          {workflow.governance_steps?.map((step, i) => (
            <GovernanceStep key={step.step_id} step={step} isExpanded={true} />
          ))}
        </div>
      </div>

      {/* Handoffs */}
      {workflow.handoffs?.length > 0 && (
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">
            Governed Handoffs
          </div>
          <div className="space-y-2">
            {workflow.handoffs?.map((handoff, i) => (
              <HandoffDisplay key={handoff.handoff_id} handoff={handoff} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerPanel;
